/**
 * Описание бизнес-логики на
 * https://kodixauto.atlassian.net/wiki/spaces/NW/pages/2246115455/-
 */

import { forward, guard, sample } from 'effector'
import {
  $areCitiesSelected,
  $cities,
  $cityWasJustAutoDetected,
  $fetching,
  $selectedCities,
  $subscribeChangesCitiesInHeader,
  acceptAutoDetectedCity,
  fetchCitiesFx,
  getFromURLFx,
  getNearest,
  getValidCityFromStorageFx,
  setSelectedCities,
  subscribeChangesCitiesInHeader,
  unsubscribeChangesCitiesInHeader,
  useAutoDetectedCities,
  useGeoAutoDetectedCities,
  useSubscribeChangesCitiesInHeader,
  writeToStorageFx,
  writeToURLFx
} from 'features/city-select/model/units'

/** Индикатор загрузки при завершении запроса ставим в false **/
$fetching.on(fetchCitiesFx.done, () => false)

/** Принимаем данные о всех доступных городах **/
$cities.on(fetchCitiesFx.doneData, (_, cities) => cities.cities)

/**
 * Когда заканчивается загрузка городов и автооопределения, пытаемся взять выбраные города из URL
 */
sample({
  source: $cities,
  clock: fetchCitiesFx.doneData,
  target: getFromURLFx
})

sample({
  source: getFromURLFx.doneData,
  clock: getFromURLFx.doneData,
  target: [writeToStorageFx, setSelectedCities]
})

/**
 * Если в УРЛ города не найдены - пытаемся взять их из локалстораджа
 */
sample({
  source: $cities,
  clock: getFromURLFx.fail,
  target: getValidCityFromStorageFx
})

/**
 * Если в локалсторадже города не найдены - будет использован автоопределённый город
 */
forward({
  from: getValidCityFromStorageFx.fail,
  to: useAutoDetectedCities
})

forward({
  from: getNearest.doneData,
  to: useGeoAutoDetectedCities
})

/**
 * Этот стор заполняется когда было вызвано событие использования автоопределённого города
 */
const $autoDetectedCities = sample({
  source: fetchCitiesFx.doneData,
  clock: [useAutoDetectedCities],
  fn: (data) => [data.nearest]
})

const $nearestCity = sample({
  source: fetchCitiesFx.doneData,
  clock: [useGeoAutoDetectedCities],
  fn: (data) => [data.nearest]
})

/**
 * Если было вызвано событие автоопределённого города, устанавливаем флаг того, что только что произошло автоопределение
 */
$cityWasJustAutoDetected.on(useAutoDetectedCities, () => true)
$cityWasJustAutoDetected.on(acceptAutoDetectedCity, () => false)

/**
 * При успешном взятии городов из УРЛ или из локалстораджа, или при событиях на их изменение (через автодетект или без) - записываем их как выбраные
 */
$selectedCities.on([getValidCityFromStorageFx.doneData, setSelectedCities], (_, data) => data)

$selectedCities.on([$autoDetectedCities], (_, data) => data)

$selectedCities.on([$nearestCity], (_, data) => data)

/**
 * Если в url нет города, то подписываемся на изменения города в шапке
 */
$subscribeChangesCitiesInHeader.on(getFromURLFx.fail, (_, data) => {
  return !!data
})

/**
 *Отписываемся от изменений города в шапке, если в фильтре был произведен выбор города
 */
$subscribeChangesCitiesInHeader.on(unsubscribeChangesCitiesInHeader, () => {
  document.removeEventListener('@kodix/one/locator-updated', subscribeChangesCitiesInHeader)
  return false
})

/**
 * Если в url нет городов, то мы подписываемся на изменение шапки
 */
guard({
  source: $subscribeChangesCitiesInHeader,
  filter: (value) => value,
  target: useSubscribeChangesCitiesInHeader
})

/**
 * При успешном взятии городов из УРЛ, локалстораджа или с помощью автодекта - указываем, что города установлены
 */
$areCitiesSelected.on([getFromURLFx.doneData, getValidCityFromStorageFx.doneData, $autoDetectedCities], () => true)

/**
 * Выбраные города пишутся в УРЛ страницы
 */
sample({
  source: $selectedCities,
  clock: [$nearestCity, setSelectedCities],
  target: writeToURLFx
})

/**
 * Значение выбраного города всегда записываем в localstorage
 */
forward({
  from: $selectedCities,
  to: writeToStorageFx
})
